import { create } from 'zustand';
import type {
  EditorTool,
  Room,
  GameObject,
  Puzzle,
  GameConfig,
  WorldType,
} from '../types';
import { generateId } from '../utils';

// ============================================
// EDITOR STORE STATE
// ============================================

interface EditorStoreState {
  // Project state
  config: GameConfig | null;
  isDirty: boolean;
  fileName: string | null;

  // Selection
  selectedRoomId: string | null;
  selectedObjectId: string | null;
  selectedPuzzleId: string | null;

  // View state
  currentTool: EditorTool;
  showGrid: boolean;
  showSpectralOverlay: boolean;
  previewWorld: WorldType;
  isPlaying: boolean;

  // Clipboard
  clipboard: GameObject | null;

  // History (for undo/redo)
  history: GameConfig[];
  historyIndex: number;

  // Actions - Project
  newProject: () => void;
  loadProject: (config: GameConfig, fileName?: string) => void;
  saveProject: () => GameConfig | null;
  markDirty: () => void;

  // Actions - Room
  addRoom: (room?: Partial<Room>) => string;
  updateRoom: (roomId: string, updates: Partial<Room>) => void;
  deleteRoom: (roomId: string) => void;
  selectRoom: (roomId: string | null) => void;

  // Actions - Object
  addObject: (roomId: string, object?: Partial<GameObject>) => string;
  updateObject: (roomId: string, objectId: string, updates: Partial<GameObject>) => void;
  deleteObject: (roomId: string, objectId: string) => void;
  selectObject: (objectId: string | null) => void;
  copyObject: (roomId: string, objectId: string) => void;
  pasteObject: (roomId: string) => void;

  // Actions - Puzzle
  addPuzzle: (roomId: string, puzzle?: Partial<Puzzle>) => string;
  updatePuzzle: (roomId: string, puzzleId: string, updates: Partial<Puzzle>) => void;
  deletePuzzle: (roomId: string, puzzleId: string) => void;
  selectPuzzle: (puzzleId: string | null) => void;

  // Actions - View
  setTool: (tool: EditorTool) => void;
  toggleGrid: () => void;
  toggleSpectralOverlay: () => void;
  setPreviewWorld: (world: WorldType) => void;
  togglePlay: () => void;

  // Actions - History
  undo: () => void;
  redo: () => void;
  pushHistory: () => void;
}

// ============================================
// DEFAULT TEMPLATES
// ============================================

const createDefaultRoom = (id: string): Room => ({
  id,
  name: 'New Room',
  description: '',
  size: [10, 8, 10],
  ambiance: {
    realWorld: {
      backgroundColor: '#FDF6F0',
      ambientLightColor: '#FFF5E6',
      ambientLightIntensity: 0.6,
    },
    spectralWorld: {
      backgroundColor: '#1C2833',
      ambientLightColor: '#4A6B8A',
      ambientLightIntensity: 0.3,
    },
  },
  objects: [],
  puzzles: [],
  connections: [],
  initialWorld: 'real',
  hasSpectralLayer: true,
});

const createDefaultObject = (id: string): GameObject => ({
  id,
  name: 'New Object',
  type: 'static',
  modelId: 'cube',
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
  visibleInWorld: 'both',
  interactableInWorld: 'both',
  properties: {},
});

const createDefaultPuzzle = (id: string): Puzzle => ({
  id,
  name: 'New Puzzle',
  type: 'switch',
  state: 'active',
  description: '',
  linkedObjectIds: [],
  solution: { type: 'state', expected: {} },
  onSolve: [],
});

const createDefaultConfig = (): GameConfig => {
  const roomId = generateId('room');
  return {
    title: 'Untitled Project',
    version: '0.1.0',
    startingRoomId: roomId,
    character: {
      id: 'player',
      name: 'Hero',
      modelId: 'character_default',
      animations: {
        idle: 'idle',
        walk: 'walk',
        interact: 'interact',
      },
      moveSpeed: 4,
    },
    rooms: [createDefaultRoom(roomId)],
  };
};

// ============================================
// EDITOR STORE
// ============================================

export const useEditorStore = create<EditorStoreState>((set, get) => ({
  // Initial state
  config: null,
  isDirty: false,
  fileName: null,
  selectedRoomId: null,
  selectedObjectId: null,
  selectedPuzzleId: null,
  currentTool: 'select',
  showGrid: true,
  showSpectralOverlay: false,
  previewWorld: 'real',
  isPlaying: false,
  clipboard: null,
  history: [],
  historyIndex: -1,

  // Project actions
  newProject: () => {
    const config = createDefaultConfig();
    set({
      config,
      isDirty: false,
      fileName: null,
      selectedRoomId: config.startingRoomId,
      selectedObjectId: null,
      selectedPuzzleId: null,
      history: [config],
      historyIndex: 0,
    });
  },

  loadProject: (config, fileName) => {
    set({
      config,
      isDirty: false,
      fileName: fileName || null,
      selectedRoomId: config.startingRoomId,
      selectedObjectId: null,
      selectedPuzzleId: null,
      history: [config],
      historyIndex: 0,
    });
  },

  saveProject: () => {
    const { config } = get();
    if (config) {
      set({ isDirty: false });
    }
    return config;
  },

  markDirty: () => set({ isDirty: true }),

  // Room actions
  addRoom: (partial) => {
    const { config, pushHistory } = get();
    if (!config) return '';

    const id = generateId('room');
    const room = { ...createDefaultRoom(id), ...partial, id };

    pushHistory();
    set({
      config: { ...config, rooms: [...config.rooms, room] },
      selectedRoomId: id,
      isDirty: true,
    });
    return id;
  },

  updateRoom: (roomId, updates) => {
    const { config, pushHistory } = get();
    if (!config) return;

    pushHistory();
    set({
      config: {
        ...config,
        rooms: config.rooms.map((r) =>
          r.id === roomId ? { ...r, ...updates } : r
        ),
      },
      isDirty: true,
    });
  },

  deleteRoom: (roomId) => {
    const { config, pushHistory, selectedRoomId } = get();
    if (!config || config.rooms.length <= 1) return;

    pushHistory();
    const newRooms = config.rooms.filter((r) => r.id !== roomId);
    const newStartingRoomId =
      config.startingRoomId === roomId ? newRooms[0].id : config.startingRoomId;

    set({
      config: { ...config, rooms: newRooms, startingRoomId: newStartingRoomId },
      selectedRoomId: selectedRoomId === roomId ? newRooms[0].id : selectedRoomId,
      isDirty: true,
    });
  },

  selectRoom: (roomId) => set({ selectedRoomId: roomId, selectedObjectId: null, selectedPuzzleId: null }),

  // Object actions
  addObject: (roomId, partial) => {
    const { config, pushHistory } = get();
    if (!config) return '';

    const id = generateId('obj');
    const object = { ...createDefaultObject(id), ...partial, id };

    pushHistory();
    set({
      config: {
        ...config,
        rooms: config.rooms.map((r) =>
          r.id === roomId ? { ...r, objects: [...r.objects, object] } : r
        ),
      },
      selectedObjectId: id,
      isDirty: true,
    });
    return id;
  },

  updateObject: (roomId, objectId, updates) => {
    const { config, pushHistory } = get();
    if (!config) return;

    pushHistory();
    set({
      config: {
        ...config,
        rooms: config.rooms.map((r) =>
          r.id === roomId
            ? {
                ...r,
                objects: r.objects.map((o) =>
                  o.id === objectId ? { ...o, ...updates } : o
                ),
              }
            : r
        ),
      },
      isDirty: true,
    });
  },

  deleteObject: (roomId, objectId) => {
    const { config, pushHistory, selectedObjectId } = get();
    if (!config) return;

    pushHistory();
    set({
      config: {
        ...config,
        rooms: config.rooms.map((r) =>
          r.id === roomId
            ? { ...r, objects: r.objects.filter((o) => o.id !== objectId) }
            : r
        ),
      },
      selectedObjectId: selectedObjectId === objectId ? null : selectedObjectId,
      isDirty: true,
    });
  },

  selectObject: (objectId) => set({ selectedObjectId: objectId, selectedPuzzleId: null }),

  copyObject: (roomId, objectId) => {
    const { config } = get();
    if (!config) return;

    const room = config.rooms.find((r) => r.id === roomId);
    const object = room?.objects.find((o) => o.id === objectId);
    if (object) {
      set({ clipboard: { ...object } });
    }
  },

  pasteObject: (roomId) => {
    const { clipboard, addObject } = get();
    if (!clipboard) return;

    addObject(roomId, {
      ...clipboard,
      id: undefined as unknown as string,
      name: `${clipboard.name} (copy)`,
      position: [clipboard.position[0] + 1, clipboard.position[1], clipboard.position[2] + 1],
    });
  },

  // Puzzle actions
  addPuzzle: (roomId, partial) => {
    const { config, pushHistory } = get();
    if (!config) return '';

    const id = generateId('puzzle');
    const puzzle = { ...createDefaultPuzzle(id), ...partial, id };

    pushHistory();
    set({
      config: {
        ...config,
        rooms: config.rooms.map((r) =>
          r.id === roomId ? { ...r, puzzles: [...r.puzzles, puzzle] } : r
        ),
      },
      selectedPuzzleId: id,
      isDirty: true,
    });
    return id;
  },

  updatePuzzle: (roomId, puzzleId, updates) => {
    const { config, pushHistory } = get();
    if (!config) return;

    pushHistory();
    set({
      config: {
        ...config,
        rooms: config.rooms.map((r) =>
          r.id === roomId
            ? {
                ...r,
                puzzles: r.puzzles.map((p) =>
                  p.id === puzzleId ? { ...p, ...updates } : p
                ),
              }
            : r
        ),
      },
      isDirty: true,
    });
  },

  deletePuzzle: (roomId, puzzleId) => {
    const { config, pushHistory, selectedPuzzleId } = get();
    if (!config) return;

    pushHistory();
    set({
      config: {
        ...config,
        rooms: config.rooms.map((r) =>
          r.id === roomId
            ? { ...r, puzzles: r.puzzles.filter((p) => p.id !== puzzleId) }
            : r
        ),
      },
      selectedPuzzleId: selectedPuzzleId === puzzleId ? null : selectedPuzzleId,
      isDirty: true,
    });
  },

  selectPuzzle: (puzzleId) => set({ selectedPuzzleId: puzzleId, selectedObjectId: null }),

  // View actions
  setTool: (tool) => set({ currentTool: tool }),
  toggleGrid: () => set((s) => ({ showGrid: !s.showGrid })),
  toggleSpectralOverlay: () => set((s) => ({ showSpectralOverlay: !s.showSpectralOverlay })),
  setPreviewWorld: (world) => set({ previewWorld: world }),
  togglePlay: () => set((s) => ({ isPlaying: !s.isPlaying })),

  // History actions
  pushHistory: () => {
    const { config, history, historyIndex } = get();
    if (!config) return;

    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(config)));

    // Keep max 50 history entries
    if (newHistory.length > 50) {
      newHistory.shift();
    }

    set({ history: newHistory, historyIndex: newHistory.length - 1 });
  },

  undo: () => {
    const { history, historyIndex } = get();
    if (historyIndex <= 0) return;

    const newIndex = historyIndex - 1;
    set({
      config: JSON.parse(JSON.stringify(history[newIndex])),
      historyIndex: newIndex,
      isDirty: true,
    });
  },

  redo: () => {
    const { history, historyIndex } = get();
    if (historyIndex >= history.length - 1) return;

    const newIndex = historyIndex + 1;
    set({
      config: JSON.parse(JSON.stringify(history[newIndex])),
      historyIndex: newIndex,
      isDirty: true,
    });
  },
}));
